import { getConnectorProviderLogoWithFallback } from "@app/lib/connector_providers_ui";
import { getVisualForDataSourceViewContentNode } from "@app/lib/content_nodes";
import {
  isFolder,
  isPodFilesDataSource,
  isWebsite,
} from "@app/lib/data_sources";
import type { DataSourceViewContentNode } from "@app/lib/swr/search";
import {
  AlertCircle,
  AttachmentChip,
  Chip,
  DoubleIcon,
  Icon,
} from "@dust-tt/sparkle";
import type React from "react";

type KnowledgeNode = Omit<
  DataSourceViewContentNode,
  "dataSourceViews" | "dataSource"
> & {
  dataSourceView: DataSourceViewContentNode["dataSourceViews"][number];
};

interface KnowledgeChipProps {
  color?: React.ComponentProps<typeof AttachmentChip>["color"];
  node: KnowledgeNode;
  onRemove?: () => void;
  title: string;
}

export function KnowledgeChip({
  color = "primary",
  node,
  title,
  onRemove,
}: KnowledgeChipProps) {
  const icon =
    isWebsite(node.dataSourceView.dataSource) ||
    isFolder(node.dataSourceView.dataSource) ||
    isPodFilesDataSource(node.dataSourceView.dataSource)
      ? getVisualForDataSourceViewContentNode(node)
      : () => (
          <DoubleIcon
            size="sm"
            mainIcon={getVisualForDataSourceViewContentNode(node)}
            secondaryIcon={getConnectorProviderLogoWithFallback({
              provider: node.dataSourceView.dataSource.connectorProvider,
            })}
          />
        );

  return (
    <AttachmentChip
      label={title}
      icon={{ visual: icon }}
      target="_blank"
      href={node.sourceUrl ?? undefined}
      color={color}
      onRemove={onRemove}
      size="xs"
    />
  );
}

interface InlineKnowledgeChipProps {
  color?: React.ComponentProps<typeof Chip>["color"];
  node: KnowledgeNode;
  onRemove?: () => void;
  title: string;
}

interface InlineKnowledgeIconProps {
  node: KnowledgeNode;
}

function InlineKnowledgeIcon({ node }: InlineKnowledgeIconProps) {
  if (
    isWebsite(node.dataSourceView.dataSource) ||
    isFolder(node.dataSourceView.dataSource) ||
    isPodFilesDataSource(node.dataSourceView.dataSource)
  ) {
    return (
      <Icon visual={getVisualForDataSourceViewContentNode(node)} size="xs" />
    );
  }

  return (
    <DoubleIcon
      size="sm"
      mainIcon={getVisualForDataSourceViewContentNode(node)}
      secondaryIcon={getConnectorProviderLogoWithFallback({
        provider: node.dataSourceView.dataSource.connectorProvider,
      })}
    />
  );
}

export function InlineKnowledgeChip({
  color = "primary",
  node,
  title,
  onRemove,
}: InlineKnowledgeChipProps) {
  const children = <InlineKnowledgeIcon node={node} />;

  if (node.sourceUrl) {
    return (
      <Chip
        label={title}
        href={node.sourceUrl}
        target="_blank"
        color={color}
        onRemove={onRemove}
        size="xs"
        className="max-w-44"
      >
        {children}
      </Chip>
    );
  }

  return (
    <Chip
      label={title}
      color={color}
      onRemove={onRemove}
      size="xs"
      className="max-w-44"
    >
      {children}
    </Chip>
  );
}

interface KnowledgeErrorChipProps {
  errorMessage?: string;
  onRemove?: () => void;
  title: string;
}

export function KnowledgeErrorChip({
  onRemove,
  title,
}: KnowledgeErrorChipProps) {
  return (
    <Chip
      label={title}
      icon={AlertCircle}
      color="primary"
      onRemove={onRemove}
      size="xs"
      className="max-w-44"
    />
  );
}
